
import { LineItemStatusSummary } from "@/models/OrderLineItemSummary";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
export default {
	name: "LineItemStatusEntry",
	components: { SystemCategoryIcon },
	props: {
		item: {
			type: LineItemStatusSummary,
			required: true,
		},
		level: {
			type: Number,
			required: true,
		},
	},
	data() {
		return {
			showDatePicker: false,
			selectedDate: null as string | null,
			manualShipDialog: false,
			manualShipLoading: false,
		};
	},
	methods: {
		isDoesNotProcess(item: LineItemStatusSummary) {
			return this.$systemCategories.doesNotProcess(item.systemCategoryId);
		},
		canManuallyShip(item: LineItemStatusSummary) {
			const user = this.$store.getters.userInfo;
			return (
				user?.isAdmin &&
				!item.isShipped &&
				!this.isDoesNotProcess(item)
			);
		},
		onDateSelected(date: string) {
			this.selectedDate = date;
			this.showDatePicker = false;
			this.manualShipDialog = true;
		},
		async executeManualShip() {
			this.manualShipLoading = true;
			try {
				await this.$http.post(`/api/orders/line-items/${this.item.lineItemId}/manually-ship`, {
					shipDate: this.selectedDate,
				});
				this.manualShipDialog = false;
				this.$bus.$emit("print-pickTicket");
			} catch (error) {
				console.error("Failed to manually ship line item:", error);
				this.$bus.$emit("showAlert", {
					message: "Failed to mark line item as shipped. Please try again.",
					type: "error",
				});
			} finally {
				this.manualShipLoading = false;
			}
		},
		cancelManualShip() {
			this.manualShipDialog = false;
			this.selectedDate = null;
		},
		renderShippingStatus(item: LineItemStatusSummary) {
			let shipStatus = {
				label: "",
				color: "",
			};

			if (this.isDoesNotProcess(item)) {
				// Not rendered since #1029 (the badge is hidden for these lines); kept so any other
				// caller still gets a sensible answer.
				shipStatus.label = "N/A";
				shipStatus.color = "table-status-gray";
			} else if (item.pickTicketType === 'PROCESSING') {
				shipStatus.label = item.processingDate
					? `Processing ${this.$formatDate(item.processingDate)}`
					: "Processing";
				shipStatus.color = "table-status-orange";
			} else if (this.isPackage(item) && item.children.every((child) => child.isShipped)) {
				shipStatus.label = "Shipped";
				shipStatus.color = "table-status-green";
			} else if (item.isShipped) {
				shipStatus.label = "Shipped";
				shipStatus.color = "table-status-green";
			} else if (item.dropShip && item.dropShipShipped > 0) {
				shipStatus.label = "Partially Shipped";
				shipStatus.color = "table-status-orange";
			} else {
				shipStatus.label = "Not Shipped";
				shipStatus.color = "table-status-gray";
			}

			return shipStatus;
		},
		isPackage(item: LineItemStatusSummary) {
			return this.$systemCategories.isPackage(item.systemCategoryId);
		},
		trackingDisplayEntries(): { tn: string; isReturn: boolean }[] {
			const entries: { tn: string; isReturn: boolean }[] = [];
			const seen = new Set<string>();
			const push = (raw: string | null | undefined, isReturn: boolean) => {
				if (!raw) return;
				// PickTicket.TrackingNumber arrives as a comma-joined string for
				// multi-package shipments — split it so each number is its own entry.
				for (const part of raw.split(",")) {
					const tn = part.trim();
					if (!tn || seen.has(tn)) continue;
					seen.add(tn);
					entries.push({ tn, isReturn });
				}
			};
			(this.item.trackingNumbers || []).forEach((tn: string) => push(tn, false));
			(this.item.returnTrackingNumbers || []).forEach((tn: string) => push(tn, true));
			return entries;
		},
		// Consolidated carrier page for every number on the line — UPS et al.
		// accept multiple space-separated tracking numbers in one URL.
		allTrackingUrl(): string {
			return this.buildTrackingUrl(
				this.trackingDisplayEntries()
					.map((e: { tn: string }) => e.tn)
					.join(" ")
			);
		},
		filterEmptyStrings(trackingNumbers: string[]): string[] {
			return trackingNumbers.filter(
				(trackingNumber) => trackingNumber && trackingNumber?.trim() !== ""
			);
		},
		detectCarrier(trackingNumber: string): string {
			const cleaned = trackingNumber.replace(/\s/g, "").toUpperCase();

			// UPS: 1Z followed by 16 characters
			if (/^1Z[A-Z0-9]{16}$/.test(cleaned)) return "ups";

			// FedEx: 12, 15, 20, or 22 digits
			if (/^\d{12}$/.test(cleaned) || /^\d{15}$/.test(cleaned) ||
				/^\d{20}$/.test(cleaned) || /^\d{22}$/.test(cleaned)) return "fedex";

			// USPS: 20-34 digits or starts with specific prefixes
			if (/^\d{20,34}$/.test(cleaned) || /^(94|93|92|91|90)\d{18,}$/.test(cleaned)) return "usps";

			return "ups";
		},
		buildTrackingUrl(trackingNumber: string): string {
			const carrier = this.detectCarrier(trackingNumber);
			switch (carrier) {
				case "ups":
					return `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber.replace(/,/g, "")}`;
				case "fedex":
					return `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${trackingNumber}`;
				case "usps":
					return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`;
				default:
					return `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`;
			}
		},
	},
};
